import { accessTokenStore } from './accessTokenStore';
import { isTokenExpiringSoon } from '../utils/jwt';

/** Minimal shape of the store slice this helper needs; keeps the dynamic import testable. */
interface RefreshingStore {
  getState(): { refreshAccessToken: () => Promise<string> };
}

let storeLoader: () => Promise<RefreshingStore> = async () => {
  const { useStore } = await import('../../store');
  return useStore as unknown as RefreshingStore;
};

/** Test hook: replace the store loader (the real one imports the Zustand store lazily). */
export function setStoreLoaderForTests(loader: (() => Promise<RefreshingStore>) | null): void {
  storeLoader = loader ?? (async () => {
    const { useStore } = await import('../../store');
    return useStore as unknown as RefreshingStore;
  });
}

/**
 * In development the backend may run on the mock clock (TimeControlController); tokens are then
 * issued against that clock and must be checked against it, not against the real time.
 */
async function currentNowMs(): Promise<number | undefined> {
  if (!import.meta.env.DEV) {
    return undefined;
  }
  try {
    const { timeProvider } = await import('../services/TimeProvider');
    return (await timeProvider.getCurrentTime()).getTime();
  } catch {
    return undefined;
  }
}

/**
 * Returns an access token that is good for the request about to be sent.
 *
 * When the stored token has expired or expires within the buffer, the store's single-flight
 * refresh is awaited first and the new token returned. Before, an expired token was either
 * sent as-is (generated client: 401, and the error path logged the user out without any refresh
 * attempt) or dropped from the request (axios client: 403, treated as an auth error). Both hit
 * whoever came back to a background tab or a woken laptop, because the browser throttles the
 * proactive one-minute refresh timer (review round 5, finding 2.3).
 *
 * If the refresh fails, the old token is returned so that the request fails the normal way and
 * the response path decides about the logout.
 */
export async function ensureFreshAccessToken(nowMs?: number, bufferMinutes = 1): Promise<string | null> {
  const token = accessTokenStore.get();
  if (!token) {
    return null;
  }
  if (!isTokenExpiringSoon(token, bufferMinutes, nowMs ?? (await currentNowMs()))) {
    return token;
  }
  try {
    const store = await storeLoader();
    const refreshed = await store.getState().refreshAccessToken();
    return typeof refreshed === 'string' && refreshed.length > 0 ? refreshed : token;
  } catch (error) {
    console.warn('ensureFreshAccessToken: refresh failed, sending the current token', error);
    return token;
  }
}
